import React, { useState, useCallback, Suspense } from 'react'; // eslint-disable-line no-unused-vars
import { useTranslation } from 'react-i18next';
import { ModalElevated, Flex, Typo, Loading, Icon } from '../../../ui';
import { getModuleCapabilities } from '../moduleConfig';
import { getEditorComponent } from '../editors';

/**
 * ModuleContentModal - Modal for editing module content
 *
 * Uses the editor registry pattern to dynamically load the appropriate
 * editor component based on module type. Each editor handles its own
 * state management and calls onChange when content changes.
 *
 * To add a new editor:
 * 1. Create a new editor component in ../editors/ folder
 * 2. Register it in ../editors/index.js
 * 3. Update ../moduleConfig.js with the new contentEditor type
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Callback when modal closes
 * @param {Function} props.onSave - Callback when content is saved
 * @param {Object} props.module - The module being edited
 * @param {boolean} props.isLoading - Whether the module is being created/saved
 * @returns {JSX.Element}
 */
const ModuleContentModal = ({ open, onClose, onSave, module, isLoading = false }) => {
  const { t } = useTranslation();

  // Pending data from the editor - updated via onChange callback
  const [pendingData, setPendingData] = useState(null);

  const moduleType = module?.type?.name;
  const capabilities = getModuleCapabilities(moduleType);

  // Get the editor component from the registry
  const EditorComponent = getEditorComponent(capabilities.contentEditor);

  // Memoized onChange handler passed to editor
  const handleEditorChange = useCallback((data) => {
    setPendingData(data);
  }, []);

  const handleSave = useCallback(() => {
    if (pendingData && onSave) {
      onSave(module, pendingData);
    }
    // Parent owns closing the modal after a successful async save.
  }, [pendingData, onSave, module]);

  const handleClose = useCallback(() => {
    // Reset pending data
    setPendingData(null);
    onClose();
  }, [onClose]);

  /**
   * Renders the appropriate editor component
   * Uses Suspense for lazy-loaded editors
   */
  const renderEditor = () => {
    if (!EditorComponent) {
      return (
        <Flex direction='column' gap='20' width='100%' align='center'>
          <Typo.Text>{t('editCourse.noEditor')}</Typo.Text>
        </Flex>
      );
    }

    return (
      <Suspense fallback={<Loading />}>
        <EditorComponent module={module} initialData={module?.data} onChange={handleEditorChange} moduleType={moduleType} />
      </Suspense>
    );
  };

  if (!module) return null;

  // Get modal title from module config (i18n keys defined in moduleConfig.js)
  const isNewModule = module._isNew;
  const i18nKey = isNewModule ? capabilities.i18n?.add : capabilities.i18n?.edit;
  const modalTitle = t(i18nKey);
  const subheaderKey = isNewModule ? 'editCourse.modalSubheaderAdd' : 'editCourse.modalSubheaderEdit';
  const subheaderText = t(subheaderKey);
  const ModuleIcon = Icon[capabilities.icon] || Icon.ViewModule;

  return (
    <ModalElevated
      open={open}
      onClose={handleClose}
      showActions={true}
      onAccept={handleSave}
      acceptLabel={t('globals.save')}
      acceptIsLoading={isLoading}
      acceptIsDisabled={!pendingData}
      width='800px'
      maxWidth='lg'
      title={modalTitle}
      subtitle={subheaderText}
      icon={ModuleIcon}
    >
      <Flex direction='column' gap='20' width='100%'>
        {renderEditor()}
      </Flex>
    </ModalElevated>
  );
};

export default ModuleContentModal;
